import React, { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faClock, faSearch, faFilter, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import SEO from '../SEO';
import LandingHeader from '../landing/LandingHeader';
import { getBlogPosts, getFeaturedPosts, getPostsByCategory } from '../../data/blogPosts';
import { seoConfig } from '../../utils/seoData';
import { generateBreadcrumbJsonLd } from '../../utils/seoData';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';

const BlogPage: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore
  const { t } = useTranslation('blog');
  const i18nInstance = i18n;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  const blogPosts = useMemo(() => getBlogPosts(i18nInstance), [i18nInstance?.language]);
  const categories = useMemo(() => ['all', ...Array.from(new Set(blogPosts.map(post => post.category)))], [blogPosts]);

  const filteredPosts = useMemo(() => {
    let filtered = blogPosts;

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = getPostsByCategory(selectedCategory, i18nInstance);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return filtered.sort((a, b) =>
      new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    );
  }, [searchTerm, selectedCategory, blogPosts, i18nInstance]);

  const featuredPosts = useMemo(() => getFeaturedPosts(i18nInstance), [i18nInstance?.language]);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const paginatedPosts = filteredPosts.slice(
    (currentPage - 1) * postsPerPage,
    currentPage * postsPerPage
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'en' ? 'en-US' : 'tr-TR';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const currentLang = lang || i18n.language || 'tr';
  const pathWithoutLang = `/blog`;
  const breadcrumbJsonLd = generateBreadcrumbJsonLd([
    { name: currentLang === 'en' ? 'Home' : 'Ana Sayfa', url: `https://winnomi.com/${currentLang}/home` },
    { name: currentLang === 'en' ? 'Blog' : 'Blog', url: `https://winnomi.com/${currentLang}/blog` },
  ]);

  return (
    <>
      <SEO
        title={currentLang === 'en'
          ? 'Blog - Social Media and Digital Marketing Tips | Winnomi AI'
          : 'Blog - Sosyal Medya ve Dijital Pazarlama İpuçları | Winnomi AI'}
        description={currentLang === 'en'
          ? 'Comprehensive guides and tips on social media analytics, AI-powered content strategy, Instagram algorithm, Facebook ad optimization and more.'
          : 'sosyal medya analitiği, AI destekli içerik stratejisi, Instagram algoritması, Facebook reklam optimizasyonu ve daha fazlası hakkında kapsamlı rehberler ve ipuçları.'}
        keywords={currentLang === 'en'
          ? 'social media blog, sosyal medya marketing tips, social media strategy, content marketing, AI marketing'
          : 'sosyal medya blog, sosyal medya pazarlama ipuçları, sosyal medya stratejisi, içerik pazarlama, AI pazarlama'}
        locale={currentLang === 'en' ? 'en_US' : 'tr_TR'}
        alternateLanguages={{
          'tr': `https://winnomi.com/tr${pathWithoutLang}`,
          'en': `https://winnomi.com/en${pathWithoutLang}`,
          'x-default': `https://winnomi.com/tr${pathWithoutLang}`
        }}
        jsonLd={[
          breadcrumbJsonLd,
          {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'Winnomi AI Blog',
            description: currentLang === 'en'
              ? 'Social media and digital marketing guides'
              : 'sosyal medya ve dijital pazarlama rehberleri',
            url: `https://winnomi.com/${currentLang}/blog`,
            publisher: {
              '@type': 'Organization',
              name: 'Winnomi AI',
            },
          },
        ]}
      />
      <LandingHeader />
      <div className="min-h-screen bg-gray-900 text-white mt-[-64px] bg-gradient-to-br from-purple-900/20 via-gray-900 to-gray-900 ">
        {/* Hero Section */}
        <section className="pb-20 px-6 pt-40">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              {t('title')}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              {t('subtitle')}
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setCurrentPage(1);
                  }}
                  className={`px-6 py-2 rounded-full transition-all ${selectedCategory === category
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/5 text-gray-300 md:hover:bg-white/10'
                    }`}
                >
                  {category === 'all' ? t('all') : category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Posts */}
        {featuredPosts.length > 0 && currentPage === 1 && selectedCategory === 'all' && !searchTerm && (
          <section className="max-w-7xl mx-auto px-6 py-12">
            <h2 className="text-2xl font-bold mb-6">{t('featuredPosts')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {featuredPosts.slice(0, 3).map((post) => (
                <Link
                  key={post.id}
                  to={`/${lang}/blog/${post.slug}`}
                  className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden md:hover:border-purple-500/50 transition-all"
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.parentElement;
                        if (fallback) {
                          fallback.className = 'aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center';
                          fallback.innerHTML = '<span class="text-4xl">📝</span>';
                        }
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <span className="inline-block px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm mb-3">
                      {post.category}
                    </span>
                    <h3 className="text-xl font-semibold mb-2 md:group-hover:text-purple-400 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faCalendar} />
                          {formatDate(post.publishedAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faClock} />
                          {post.readTime} {t('common.min')}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* All Posts */}
        <section className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">
              {searchTerm ? `${t('searchResults')} (${filteredPosts.length})` : t('allPosts')}
            </h2>
            {filteredPosts.length > 0 && (
              <span className="text-gray-400">
                {filteredPosts.length} {t('postsFound')}
              </span>
            )}
          </div>

          {paginatedPosts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {paginatedPosts.map((post) => {
                  const formatDate = (dateString: string) => {
                    const date = new Date(dateString);
                    const locale = i18nInstance.language === 'en' ? 'en-US' : 'tr-TR';
                    return date.toLocaleDateString(locale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    });
                  };

                  const categoryLabels: Record<string, string> = {
                    'Analitik': t('categories.Analitik'),
                    'AI & Teknoloji': t('categories.AI & Teknoloji'),
                    'sosyal medya': t('categories.sosyal medya'),
                    'Reklam': t('categories.Reklam'),
                    'Analytics': t('categories.Analytics'),
                    'AI & Technology': t('categories.AI & Technology'),
                    'Social Media': t('categories.Social Media'),
                    'Advertising': t('categories.Advertising')
                  };

                  return (
                    <Link
                      key={post.id}
                      to={`/${lang}/blog/${post.slug}`}
                      className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden md:hover:border-purple-500/50 transition-all"
                    >
                      <div className="aspect-video relative overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.parentElement;
                            if (fallback) {
                              fallback.className = 'aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center';
                              fallback.innerHTML = '<span class="text-4xl">📝</span>';
                            }
                          }}
                        />
                      </div>
                      <div className="p-6">
                        <span className="inline-block px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm mb-3">
                          {categoryLabels[post.category] || post.category}
                        </span>
                        <h3 className="text-xl font-semibold mb-2 md:group-hover:text-purple-400 transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faCalendar} />
                              {formatDate(post.publishedAt)}
                            </span>
                            <span className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faClock} />
                              {post.readTime} {t('common.min')}
                            </span>
                          </div>
                        </div>
                        <span className="text-purple-400 md:group-hover:underline">
                          {t('common.readMore')}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed md:hover:bg-white/10 transition-all"
                  >
                    {t('previous')}
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-4 py-2 rounded-lg transition-all ${currentPage === page
                        ? 'bg-purple-600 text-white'
                        : 'bg-white/5 border border-white/10 md:hover:bg-white/10'
                        }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed md:hover:bg-white/10 transition-all"
                  >
                    {t('next')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">{t('noPostsFound')}</p>
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default BlogPage;

